import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { Clock, ArrowRight } from "lucide-react";

export default function BlogIndex() {
  const [cat, setCat] = useState("All");
  const { data: posts = [] } = useQuery({ queryKey: ["blog-index"], queryFn: async () => (await api.get("/blog")).data });
  const { data: cats = [] } = useQuery({ queryKey: ["blog-cats"], queryFn: async () => (await api.get("/blog/categories")).data });
  const filtered = cat === "All" ? posts : posts.filter((p) => p.category === cat);

  return (
    <>
      <Seo title="HVAC Resource Center & Blog | Aquila Air & Heating" description="Expert HVAC tips, AC repair guides, maintenance advice, and buying guides for Florida homeowners from the Aquila Air & Heating resource center." path="/blog/" />
      <section className="bg-navy text-white py-12 md:py-16" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog" }]} /></div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight">HVAC Resource Center</h1>
          <p className="mt-3 max-w-2xl text-lg text-slate-200">Straight-talk answers to the HVAC questions Florida homeowners ask most — from AC repair to lowering your energy bills.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-filters">
            {["All", ...cats].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c}`} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy"}`}>{c}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col">
                <div className="relative h-44 overflow-hidden">
                  <img src={p.featured_image} alt={p.image_alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  <span className="absolute top-3 left-3 bg-launch text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.category}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="text-lg font-semibold text-navy leading-snug group-hover:text-launch transition-colors">{p.title}</h2>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2 flex-1">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> {p.read_time} read</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBlock />
    </>
  );
}
